import React, { useState, useEffect } from 'react';
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import Header from '../components/Header';
import TimelineView from '../components/TimelineView';
import CalendarView from '../components/CalendarView';

function Dashboard({ onLogout, isLocalMode }) {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('timeline');
  const [journey, setJourney] = useState('pregnancy'); // 'pregnancy' or 'growth'
  const [refreshData, setRefreshData] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);

  useEffect(() => {
    getUser();
  }, []);

  const getUser = async () => {
    try {
      if (isLocalMode) {
        // In local mode, set a mock user
        setUser({ username: 'Local User', email: 'user@local.dev' });
      } else {
        const currentUser = await getCurrentUser();
        setUser(currentUser);
      }
    } catch (error) {
      console.error('Error getting user:', error);
    }
  };

  const handleLogout = async () => {
    try {
      if (!isLocalMode) {
        await signOut();
      }
      onLogout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleRefresh = () => {
    setRefreshData(!refreshData);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={user} onLogout={handleLogout} />

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-2">
            {/* Journey Selection */}
            <div className="flex gap-2 mr-6">
              <button
                onClick={() => setJourney('pregnancy')}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  journey === 'pregnancy'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                🤰 Pregnancy Journey
              </button>
              <button
                onClick={() => setJourney('growth')}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  journey === 'growth'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                👶 Growth Journey
              </button>
            </div>

            {/* View Selection */}
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('timeline')}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  activeTab === 'timeline'
                    ? 'bg-pink-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📅 Timeline
              </button>
              <button
                onClick={() => setActiveTab('calendar')}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  activeTab === 'calendar'
                    ? 'bg-pink-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📆 Calendar
              </button>
            </div>

            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              className="ml-auto px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition"
            >
              🔄 Refresh
            </button>

            {/* Date Filter Toggle */}
            <button
              onClick={() => setShowDateFilter(!showDateFilter)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition"
            >
              🗓️ Filter by Date
            </button>
          </div>

          {/* Date Range Filter */}
          {showDateFilter && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <button
                  onClick={() => {
                    setStartDate('');
                    setEndDate('');
                  }}
                  className="px-4 py-2 bg-gray-400 text-white rounded-lg font-medium hover:bg-gray-500 transition"
                >
                  Clear
                </button>
                <button
                  onClick={handleRefresh}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition"
                >
                  Apply Filter
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'timeline' && (
          <TimelineView journey={journey} refresh={refreshData} startDate={startDate} endDate={endDate} />
        )}
        {activeTab === 'calendar' && (
          <CalendarView journey={journey} refresh={refreshData} startDate={startDate} endDate={endDate} />
        )}
      </div>
    </div>
  );
}

export default Dashboard;
